import { colors } from '@/constants/colors'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'

/**
 * How big the marks are set, and everything that has to move with them.
 *
 * `gutter` is the sheet's, and the default: a 19pt numeral in a 24pt column, a quiet index to a
 * list of benefits the reader is meant to read rather than count. `display` is the kit page's,
 * where the numerals are the only ornament on the card and are set at 32 in the display face —
 * which means a wider column to hold two figures, and the mark centred on the whole item rather
 * than hung off its first line, so a two-line entry reads as one block beside one numeral.
 *
 * One prop rather than three, because the three never move independently: a 32pt numeral in a
 * 24pt gutter overlaps the words, and a small numeral centred on a two-line item floats.
 */
export type LedgerScale = 'gutter' | 'display'

/**
 * `mark` is the gutter's width, `line` the box the mark is set in — so a plus and a numeral make
 * a row of the same height — `gap` the space to the words, and `size` the type.
 */
const SCALE: Record<
	LedgerScale,
	{ mark: number; line: number; gap: number; size: number }
> = {
	gutter: { mark: 24, line: 22, gap: 14, size: 19 },
	display: { mark: 42, line: 32, gap: 14, size: 32 },
}

/**
 * The plus is drawn on the maths axis rather than to cap height, so at the gutter's size it
 * needs a size up to carry the same weight as a numeral beside it. At display size it is already
 * large enough that the difference stops reading, so it takes the same size as the numerals.
 */
const PLUS_SIZE: Record<LedgerScale, number> = { gutter: 24, display: 32 }

/**
 * A row carries its padding on both sides so the rule between two of them is evenly spaced, but
 * the first row drops its top so every label sits the same distance above what it heads —
 * whether that is a list or a paragraph.
 */
const ROW = 12

export interface LedgerItem {
	title: string
	/** A second line under the title, in the face a row uses for its own. */
	line?: string
}

/**
 * A ledger: a mark in the gutter, then what it is.
 *
 * The sheet's list. What a plan covers is a finite, numbered set, so those carry numerals; what
 * a member can add is open-ended, so those carry a plus. The rules start at the text rather
 * than the gutter, so the marks read as a column of their own.
 */
export const Ledger = ({
	items,
	mark,
	scale = 'gutter',
}: {
	items: readonly (string | LedgerItem)[]
	mark: (index: number) => string
	/** How big the marks are set, and how far the words clear them. See `LedgerScale`. */
	scale?: LedgerScale
}) => {
	const { mark: MARK, line: MARK_LINE, gap: GAP, size } = SCALE[scale]
	const TEXT = MARK + GAP
	// At display size the mark is taller than one line of body type, so the row centres its two
	// children against each other and the text block sizes to its own content. At gutter size the
	// old behaviour holds: both boxes stretch, and each one centres what it holds inside itself.
	const centred = scale === 'display'

	return (
		<View>
			{items.map((item, index) => {
				const { title, line } = typeof item === 'string' ? { title: item } : item
				return (
					<View key={title}>
						{index > 0 && (
							<View
								className="h-px"
								style={{ backgroundColor: colors.divider, marginLeft: TEXT }}
							/>
						)}
						<View
							className="flex-row"
							style={{
								gap: GAP,
								paddingTop: index === 0 ? 0 : ROW,
								paddingBottom: ROW,
								alignItems: centred ? 'center' : 'stretch',
							}}
						>
							{/* The mark is held in a box of a fixed height: a larger glyph in a shared
							    line box makes its own row taller, and the add-ons stood a third of a
							    line deeper than the numbered list above them. The box is the line,
							    whatever is set in it. */}
							<View
								style={{
									width: MARK,
									height: MARK_LINE,
									alignItems: 'center',
									justifyContent: 'center',
								}}
							>
								<Text
									className="font-display tracking-[-0.02em]"
									style={{
										color: colors.accent,
										fontSize: mark(index) === '+' ? PLUS_SIZE[scale] : size,
										// Pinned only where the type is taller than the box it is set
										// in: at 32 the font's own line box exceeds the 32pt gutter row
										// and iOS clips the numeral's top. At gutter size the face's
										// default leading already fits, and is left where it was so the
										// membership sheet's ledger is the pixels it was yesterday.
										lineHeight: centred ? MARK_LINE : undefined,
									}}
								>
									{mark(index)}
								</Text>
							</View>
							{/* A one-line item is centred on its mark; a pair is set from the top, so the
							    title keeps the mark's line and the line beneath hangs off it. */}
							<View
								className="flex-1"
								style={{
									justifyContent: line ? 'flex-start' : 'center',
									minHeight: MARK_LINE,
								}}
							>
								<Text size="body1" weight="bold">
									{title}
								</Text>
								{!!line && (
									<Text size="body1" tone="soft" className="mt-0.5">
										{line}
									</Text>
								)}
							</View>
						</View>
					</View>
				)
			})}
		</View>
	)
}

/** The numeral a counted list's nth item wears: 01, 02, 03. */
export const numbered = (index: number) => String(index + 1).padStart(2, '0')

/** What an open-ended list's items wear instead of a count. */
export const plus = () => '+'
